import dayjs from 'dayjs'
import capitalize from 'lodash/capitalize'
import { BOOKING_SESSION_STATUS, MOMENT_FORMATS, getSessionDisplayLabel } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'
import { getExactTimeDiff } from 'src/consumer/helpers/utilFunctions'
import { preparePatientLabelFromPatientInfoObject } from 'src/supportPortal/views/scheduledPatients/utils'
import { shouldShowPrescriptionPdfDownloadButtons } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import ContactDetailsTableItem from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/ContactDetailsTableItem'
import InTherapyButton from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/InTherapyButton'
import ConsultsActions from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsActions'
import ConsultsDetailsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsDetailsCell'
import ConsultsVitalsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsVitalsCell'

// Chamber-drawer status palette: accent bar + time + pill share a colour per state.
const SLIM_STATUS = {
  'in-progress': { label: 'In progress', color: '#0f8a5f', bg: 'rgba(16, 185, 129, 0.15)', accent: '#10b981' },
  completed: { label: 'Completed', color: 'rgba(15, 23, 42, 0.55)', bg: 'rgba(15, 23, 42, 0.06)', accent: 'rgba(15, 23, 42, 0.30)' },
  cancelled: { label: 'Cancelled', color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)', accent: '#dc2626' },
  scheduled: { label: 'Yet to start', color: '#5568d3', bg: 'rgba(103, 121, 227, 0.14)', accent: '#6779e3' },
}

function resolveSlimStatus(bookedSlot, isCompleted) {
  if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.CANCELLED) return 'cancelled'
  if (
    isCompleted ||
    bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.MARKED_COMPLETED ||
    bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.FINISHED
  ) {
    return 'completed'
  }
  if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS) return 'in-progress'
  return 'scheduled'
}

/**
 * Mobile-first card for a single appointment.
 *
 * Mentor flow (`forMentor`): a slim, single-glance card matching the chamber drawer — time on the
 * left, patient + modality·type in the middle, a status pill on the right, and a "+" actions
 * button. Tapping the card enters the session (Join/Rejoin).
 *
 * Clinic-assistant / support flow: the original full, stacked card with all the detail those desks
 * rely on (ID, contact, wait time, vitals, clinical chips).
 */
export default function ConsultsCard({
  bookedSlot,
  viewButtonClick,
  showActionButtons = false,
  hidePatientContactDetails = false,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  prescriptionViewSettings,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
}) {
  const patientInfo = bookedSlot?.patientInfo
  const visitNo = bookedSlot?.patientMeetingSeqNumber
  const meetingSrc = bookedSlot?.meetingSrc
  const isCompleted = Boolean(bookedSlot?.markedCompleted)
  const slotStart = bookedSlot?.startTime ? dayjs.utc(bookedSlot.startTime).local() : null

  const onOpenVitals = setAddVitalsDialogOpen
    ? (slot) => {
        setClickedBookedSlotData?.(slot)
        setAddVitalsDialogOpen(true)
      }
    : undefined

  const actions = showActionButtons ? (
    <ConsultsActions
      bookedSlot={bookedSlot}
      mentorProfileData={mentorProfileData}
      clinicLocationData={clinicLocationData}
      handleNewTherapyRequest={handleNewTherapyRequest}
      setClickedBookedSlotData={setClickedBookedSlotData}
      therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
      setEditPatientDialogOpen={setEditPatientDialogOpen}
      forMentor={forMentor}
      setAddVitalsDialogOpen={setAddVitalsDialogOpen}
      setEditBookingDialogOpen={setEditBookingDialogOpen}
      refreshBookedSlots={refreshBookedSlots}
      showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons({
        forMentor,
        bookedSlot,
        prescriptionViewSettingsData: prescriptionViewSettings,
      })}
      sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
      prescriptionGetApiSignature={prescriptionGetApiSignature}
      prescriptionSaveApiSignature={prescriptionSaveApiSignature}
      fileUploadApiSignature={fileUploadApiSignature}
      showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
      sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
      sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
      prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
      prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
    />
  ) : null

  // ── Mentor: slim chamber-style card ──────────────────────────────────────────────────────────
  if (forMentor) {
    const ui = SLIM_STATUS[resolveSlimStatus(bookedSlot, isCompleted)]
    const age = getAgeFromDobWithTime(patientInfo?.dateOfBirth)
    const genderInitial = patientInfo?.gender ? String(patientInfo.gender).charAt(0).toUpperCase() : ''
    const ageGender = [age, genderInitial].filter((v) => v != null && v !== '' && v !== 'NaN').join('/')
    const typeLabel = getSessionDisplayLabel(bookedSlot)
    const canJoin = Boolean(viewButtonClick)

    return (
      <div
        role={canJoin ? 'button' : undefined}
        tabIndex={canJoin ? 0 : undefined}
        onClick={canJoin ? () => viewButtonClick(bookedSlot) : undefined}
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: '12px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          borderLeft: `4px solid ${ui.accent}`,
          borderRadius: '12px',
          background: '#ffffff',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          padding: '10px 12px',
          cursor: canJoin ? 'pointer' : 'default',
        }}
      >
        {/* Time */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            minWidth: '62px',
          }}
        >
          <span style={{ fontWeight: 700, fontSize: '14px', color: ui.color, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
            {slotStart ? slotStart.format('h:mm A') : '--'}
          </span>
        </div>

        {/* Patient + modality·type */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', minWidth: 0 }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: '14px',
                color: '#0f172a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
            </span>
            {ageGender && (
              <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0 }}>{ageGender}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#475569', minWidth: 0 }}>
            {getMeetingSrcIcon(meetingSrc, { size: 14, color: ui.accent })}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getMeetingSrcLabel(meetingSrc)}
              {typeLabel ? ` · ${typeLabel}` : ''}
              {visitNo ? ` · Visit #${visitNo}` : ''}
            </span>
          </div>
        </div>

        {/* Status + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '6px', flexShrink: 0 }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: ui.color,
              background: ui.bg,
              padding: '3px 9px',
              borderRadius: '999px',
              whiteSpace: 'nowrap',
            }}
          >
            {ui.label}
          </span>
          {actions && (
            <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
              {actions}
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Clinic-assistant / support: full stacked card ────────────────────────────────────────────
  let waitContent = '--'
  const sessionStart = bookedSlot?.sessionStartedAt ? dayjs.utc(bookedSlot.sessionStartedAt).local() : null
  if (sessionStart && slotStart) {
    waitContent = getExactTimeDiff(slotStart, sessionStart, false, false, false)
  } else if (slotStart && slotStart.isSame(dayjs(), 'day')) {
    waitContent = getExactTimeDiff(slotStart, dayjs(), false, false, false)
  }

  return (
    <div
      style={{
        border: '1px solid',
        borderColor: isCompleted ? 'rgba(16, 185, 129, 0.35)' : 'rgba(99, 102, 241, 0.18)',
        borderLeft: `4px solid ${isCompleted ? '#10b981' : '#6366F1'}`,
        borderRadius: '14px',
        background: isCompleted ? 'rgba(209, 250, 229, 0.45)' : '#ffffff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Header: token · mode · time */}
      <div className="d-flex align-items-center justify-content-between" style={{ gap: '8px' }}>
        <div className="d-flex align-items-center" style={{ gap: '10px', minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: '17px', color: '#4338CA', flexShrink: 0 }}>
            #{bookedSlot?.appointmentNumber}
          </span>
          <span className="d-flex align-items-center" style={{ gap: '4px', fontSize: '12px', color: '#475569', minWidth: 0 }}>
            {getMeetingSrcIcon(meetingSrc, { size: 16, color: '#6366F1' })}
            <span style={{ whiteSpace: 'nowrap' }}>{getMeetingSrcLabel(meetingSrc)}</span>
          </span>
        </div>
        <span style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a', flexShrink: 0 }}>
          {slotStart ? slotStart.format(MOMENT_FORMATS.formattedTime24Hrs) : '--'}
        </span>
      </div>

      {/* Patient */}
      <div className="d-flex flex-column" style={{ gap: '2px' }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
          {bookedSlot?.therapyPatient && <InTherapyButton bookedSlot={bookedSlot} slotBlocked={false} />}
          <span style={{ fontWeight: 600, fontSize: '15px' }}>
            {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
          </span>
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
          {getAgeFromDobWithTime(patientInfo?.dateOfBirth)} Y / {capitalize(patientInfo?.gender)}
          {visitNo ? ` · Visit #${visitNo}` : ''}
          {patientInfo?.patientNumber ? ` · ID ${patientInfo.patientNumber}` : ''}
        </div>
        <ContactDetailsTableItem
          bookedEventInfo={bookedSlot}
          isTableItem={false}
          hidePatientContactDetails={hidePatientContactDetails}
        />
      </div>

      {/* Type, status, screening */}
      <ConsultsDetailsCell bookedSlot={bookedSlot} />

      {/* Wait + vitals */}
      <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: '8px' }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Wait: <strong style={{ color: '#334155' }}>{waitContent}</strong>
        </span>
        <ConsultsVitalsCell bookedSlot={bookedSlot} onOpenVitals={onOpenVitals} />
      </div>

      {/* Actions */}
      <div
        className="d-flex align-items-center"
        style={{ gap: '8px', borderTop: '1px solid rgba(15, 23, 42, 0.06)', paddingTop: '10px' }}
      >
        <span style={{ flex: 1, fontSize: '12px', color: '#475569', fontWeight: 600 }}>
          {isCompleted ? 'Completed' : bookedSlot?.sessionStartedAt ? 'In Progress' : 'Upcoming'}
        </span>
        {actions && <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>{actions}</div>}
      </div>
    </div>
  )
}
